/**
 * The ApnaAlgo brand, as the app wears it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO MARKS, AND THEY ARE NOT INTERCHANGEABLE
 *
 * `LogoMark` is the icon alone — the triangle and its arrow. It is pure
 * blue/cyan gradient with no near-black anywhere, so it reads on the light
 * theme and on the terminal's dark surface without a variant. That is what
 * makes it the right thing for small, repeated places: the rail, a chip, a
 * favicon.
 *
 * `BrandLockup` is the icon PLUS the wordmark, which is the canonical brand
 * and belongs in exactly one place per screen. Used twice it stops reading as
 * a logo and starts reading as a repeated image.
 *
 * WHY THE LOCKUP SHIPS TWICE
 *
 * "Apna" is set in a near-black navy (#031638), which is the brand on white
 * and invisible on `--background` in dark mode — and this app opens dark.
 * So there are two files that differ by exactly one line, the `navy` gradient
 * stops, and CSS picks one. The icon's gradients and "Algo"'s blue are
 * untouched in both: only the part that would disappear is restated.
 *
 * SWAPPED IN CSS, NOT IN JAVASCRIPT. Reading the theme in React would render
 * the wrong logo for one frame on load and again on every toggle, and the
 * flash is on the one element a person uses to recognise the product. Both
 * `<img>` are in the DOM; the variant rule shows one. The hidden one is not
 * fetched by any current browser.
 *
 * STATIC FILES RATHER THAN INLINE SVG. The artwork is ~12 KB each and
 * belongs in the asset cache, not in the JS bundle that has to parse before
 * anything renders. `logo/` holds the originals as delivered; `public/brand/`
 * holds what the app serves, and the dark variant is generated from the
 * original rather than hand-drawn.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { cn } from '@/lib/utils'

/**
 * The icon alone, theme-agnostic.
 *
 * `size` is a HEIGHT and the width follows, because the mark is 405x332 and
 * not square. Setting both would not distort it — an `<img>` scales an SVG
 * with `xMidYMid meet` — it would letterbox it, so a nominal 28px chip would
 * draw a 23px mark with dead space above and below and no way to tell from
 * the call site. Asking for the dimension that is actually honoured means
 * `size={28}` puts 28 pixels of logo on screen.
 *
 * `aria-hidden` because every call site already names itself — the rail's
 * button says "ApnaAlgo home" — and a second announcement of the same thing
 * is noise to a screen reader rather than information.
 */
export function LogoMark({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <img
      src="/brand/apnaalgo-icon.svg"
      alt=""
      aria-hidden="true"
      draggable={false}
      className={cn('w-auto select-none', className)}
      style={{ height: size }}
    />
  )
}

/**
 * Icon and wordmark together, sized by HEIGHT.
 *
 * The artwork is 1382x335 in its own units, so the width follows from the
 * height and must not be set: fixing both would letterbox or stretch the
 * one image a brand is least forgiving about.
 */
export function BrandLockup({ height = 22, className }: { height?: number; className?: string }) {
  const common = 'w-auto select-none'
  return (
    <span
      className={cn('inline-flex shrink-0 items-center', className)}
      role="img"
      aria-label="ApnaAlgo"
      data-testid="brand-lockup"
    >
      <img
        src="/brand/apnaalgo-lockup.svg"
        alt=""
        draggable={false}
        className={cn(common, 'dark:hidden')}
        style={{ height }}
      />
      <img
        src="/brand/apnaalgo-lockup-dark.svg"
        alt=""
        draggable={false}
        className={cn(common, 'hidden dark:block')}
        style={{ height }}
      />
    </span>
  )
}
